import { DeleteConfirmDialog, useDeleteConfirm } from 'app/components/deleteConfirmDialog';
import { DataItemsFetchStatus, Visual } from 'app/dataStore/types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ButtonItem,
  ColumnSettings,
  DataGrid,
  DataGridSelectionType,
  useBladeButtons,
} from 'react-tools';

import Add from '@material-ui/icons/Add';
import Delete from '@material-ui/icons/Delete';
import Edit from '@material-ui/icons/Edit';

import { IdName } from '../../../../dataStore';
import { useStyles } from './visualsList.jss';

const columnSettings: ColumnSettings[] = [
  {
    name: 'name',
    width: 190,
    sort: { order: 0, direction: 'asc' },
    lockVisibility: true,
    filter: 'agTextColumnFilter',
  },
  { name: 'workgroupName', width: 190, filter: 'agTextColumnFilter' },
];

export interface VisualsListProps {
  workgroupId: number;
  visuals: Visual[];
  fetchStatus: DataItemsFetchStatus;
  isEditing: boolean;
  visualsEditBladeId?: string;
}

export interface VisualsListActions {
  fetchVisuals: (workgroupId: number) => void;
  createVisual: () => void;
  editVisual: (visualId: number, visualName: string, workgroupId: number) => void;
  deleteVisual: (visualId: number, visualName: string, bladeId?: string) => void;
}

export const VisualsList = (props: VisualsListProps & VisualsListActions) => {
  const classes = useStyles();
  const [deleteConfirmVisual, setDeleteConfirmVisual] = useDeleteConfirm();
  const [selected, setSelected] = useState<IdName[]>([]);
  const [t] = useTranslation();

  const editVisual = useCallback(() => {
    if (selected.length) {
      props.editVisual(selected[0].id, selected[0].name, (selected[0] as Visual).workgroupId);
    }
  }, [selected, props.editVisual]);

  const deleteVisual = useCallback(() => {
    if (selected.length > 0) {
      const visual = selected[0];
      setDeleteConfirmVisual({
        id: visual.id,
        name: visual.name,
        confirmNeeded: true,
      });
    }
  }, [selected, setDeleteConfirmVisual]);

  const onVisualDelete = useCallback(() => {
    props.deleteVisual(
      deleteConfirmVisual.id as number,
      deleteConfirmVisual.name,
      props.visualsEditBladeId
    );
    setDeleteConfirmVisual({
      id: 0,
      name: '',
      confirmNeeded: false,
    });
  }, [props.deleteVisual, props.visualsEditBladeId, deleteConfirmVisual]);

  const headerButtons: ButtonItem[] = useMemo(
    () => [
      {
        icon: () => <Add />,
        tooltip: 'zoneList.tooltip.add',
        onClick: props.createVisual,
      },
      {
        icon: () => <Edit />,
        tooltip: 'zoneList.tooltip.edit',
        onClick: editVisual,
      },
      // {
      //   icon: () => <Delete />,
      //   onClick: deleteVisual,
      //   tooltip: 'zoneList.tooltip.delete',
      // },
    ],
    [props.createVisual, editVisual]
  );
  useBladeButtons(headerButtons);

  useEffect(() => {
    if (!props.fetchStatus.isFetching && !props.fetchStatus.fetchComplete) {
      props.fetchVisuals(props.workgroupId);
    }
  }, [props.workgroupId, props.fetchVisuals, props.fetchStatus]);

  const selectVisuals = useCallback((items: IdName[]) => {
    setSelected(items as IdName[]);
  }, []);

  return (
    <div className={classes.container}>
      <DataGrid
        identifier={'visuals'}
        idColumn={'id'}
        onSelect={selectVisuals}
        rowClass={classes.alignedRow}
        items={props.visuals}
        selectionType={DataGridSelectionType.SingleOrNone}
        columnSettings={columnSettings}
        showSelectionColumn
        externalFilterFn={undefined}
        isExternalFilterEnabled={undefined}
      />
      <DeleteConfirmDialog
        important={true}
        title={t<string>('newnity.zone.delete.confirm.title')}
        message={
          (props.isEditing ? t('newnity.zone.delete.currently.editing') : '') +
          t('newnity.zone.delete.confirm.message', {
            visualName:
              selected.length > 0
                ? (selected[0] as Visual).name || t('newnity.zone.delete.confirm.generic')
                : '',
          })
        }
        open={deleteConfirmVisual.confirmNeeded}
        onDelete={onVisualDelete}
        onCancel={() =>
          setDeleteConfirmVisual({
            id: 0,
            name: '',
            confirmNeeded: false,
          })
        }
      />
    </div>
  );
};
